import React, { useState } from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter, ChevronsRight, ArrowUpRight, Send, Sparkles } from 'lucide-react';
import { LOGO_URL } from '../data/mock';
import { toast } from '../hooks/use-toast';

const Footer = () => {
  const [email, setEmail] = useState('');

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({ title: 'Subscribed!', description: `Compliance updates will land at ${email}.` });
    setEmail('');
  };

  return (
    <footer className="relative bg-[#0A0E1A] text-white overflow-hidden">
      {/* Ambient */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#C41E3A]/15 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#C41E3A]/10 blur-3xl" />
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20">
        {/* CTA banner top */}
        <div className="grid lg:grid-cols-2 gap-8 items-center pb-16 border-b border-white/10">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#F87171] mb-3">
              <Sparkles className="w-3.5 h-3.5" /> READY TO START
            </div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.05]">
              Let&rsquo;s map your compliance <span className="text-[#F87171]">roadmap</span> together.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:justify-end">
            <a
              href="/contact"
              className="pill-btn group inline-flex items-center gap-2 rounded-full bg-[#C41E3A] hover:bg-[#A81830] text-white pl-6 pr-1 h-14 text-sm font-semibold"
            >
              Book free consult
              <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center pill-arrow">
                <ChevronsRight className="w-4 h-4 text-[#C41E3A]" />
              </span>
            </a>
            <a href="tel:+918851590081" className="flex items-center gap-2 text-white/80 hover:text-white text-sm">
              <Phone className="w-4 h-4 text-[#F87171]" /> 8851590081
            </a>
          </div>
        </div>

        {/* Main footer grid */}
        <div className="grid lg:grid-cols-12 gap-10 py-14">
          <div className="lg:col-span-4">
            <div className="bg-white rounded-2xl inline-block p-3 mb-5">
              <img src={LOGO_URL} alt="Complise India" className="h-10 w-auto" />
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-sm mb-6">
              A Service by Sharma Brother&rsquo;s. Regulatory execution across BIS, WPC, TEC, CDSCO, FSSAI, PESO, DGFT &amp; ARAI — documented, tracked and delivered.
            </p>

            {/* Newsletter */}
            <div className="text-[10px] uppercase tracking-widest text-[#F87171] font-semibold mb-3">Compliance Digest</div>
            <form onSubmit={subscribe} className="flex items-center bg-white/[0.04] border border-white/10 focus-within:border-[#C41E3A] rounded-full pl-5 pr-1 h-12 max-w-md transition-colors">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/40"
                required
              />
              <button type="submit" className="w-10 h-10 rounded-full bg-[#C41E3A] hover:bg-[#A81830] flex items-center justify-center transition-colors">
                <Send className="w-4 h-4 text-white" />
              </button>
            </form>
            <p className="mt-2.5 text-[10px] text-white/40">Monthly regulatory updates. No spam.</p>

            {/* Socials */}
            <div className="flex gap-2 mt-6">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
                <a key={i} href="#" className="w-10 h-10 rounded-full border border-white/15 hover:bg-[#C41E3A] hover:border-[#C41E3A] flex items-center justify-center transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-4">Navigate</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="/about" className="link-underline">About</a></li>
              <li><a href="/services" className="link-underline">Services</a></li>
              <li><a href="/industries" className="link-underline">Industries</a></li>
              <li><a href="/insights" className="link-underline">Insights</a></li>
              <li><a href="/global" className="link-underline">Global Market Access</a></li>
              <li><a href="/pages" className="link-underline">All Pages</a></li>
              <li><a href="/contact" className="link-underline">Contact</a></li>
              <li><a href="https://crm.compliseindia.com" target="_blank" rel="noopener noreferrer" className="link-underline">Portal Login</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-4">Popular Services</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="/bureau-of-indian-standards-bis" className="link-underline">BIS · ISI · CRS Certification</a></li>
              <li><a href="/wireless-planning-coordination-wpc" className="link-underline">WPC ETA · Wireless Approvals</a></li>
              <li><a href="/central-drugs-standard-control-organization-cdsco" className="link-underline">CDSCO · Medical Devices</a></li>
              <li><a href="/food-safety-standards-authority-india-fssai" className="link-underline">FSSAI · Food Licences</a></li>
              <li><a href="/pages" className="link-underline">PESO · DGFT · ARAI</a></li>
              <li><a href="/gem-registration" className="link-underline">GeM Seller Registration</a></li>
              <li><a href="/amc-for-brands" className="link-underline">AMC Compliance Management</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-4">Reach us</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#F87171]" />
                </span>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Call</div>
                  <div className="text-white font-semibold text-sm">8851590081</div>
                  <div className="text-white/70 text-xs">+91 8076349604</div>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#F87171]" />
                </span>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Email</div>
                  <div className="text-white font-semibold text-sm">info@compliseindia.com</div>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#F87171]" />
                </span>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Base</div>
                  <div className="text-white/80 text-xs leading-relaxed">India — India-led execution with global coverage</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 pb-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} Complise India · A Service by Sharma Brother&rsquo;s · All rights reserved
          </div>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <a href="/privacy-policy" className="link-underline">Privacy</a>
            <a href="/terms-and-conditions" className="link-underline">Terms</a>
            <a href="/pages" className="link-underline">Sitemap</a>
            <a href="/business-legal-compliance" className="link-underline inline-flex items-center gap-1">Compliance policy <ArrowUpRight className="w-3 h-3" /></a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
